import React from "react";
import { shuffleGameData } from "../../lib/game-helpers";
import GameGrid from "../GameGrid";
import NumberOfMistakesDisplay from "../NumberOfMistakesDisplay";
import GameLostModal from "../modals/GameLostModal";
import GameWonModal from "../modals/GameWonModal";

import { Button } from "../ui/button";
import ConfettiExplosion from "react-confetti-explosion";
import { RotateCcw } from "lucide-react";

import { PuzzleDataContext } from "../../providers/PuzzleDataProvider";
import { GameStatusContext } from "../../providers/GameStatusProvider";
import GameControlButtonsPanel from "../GameControlButtonsPanel";
import { trackGameWon, trackGameLost, trackPlayAgain } from "../../lib/analytics";

import ViewResultsModal from "../modals/ViewResultsModal";

function Game() {
  const { gameData, categorySize, numCategories } =
    React.useContext(PuzzleDataContext);
  const {
    submittedGuesses,
    solvedGameData,
    isGameOver,
    isGameWon,
    setGuessCandidate,
    setSubmittedGuesses,
    setSolvedGameData,
    setIsGameOver,
    setIsGameWon,
  } = React.useContext(GameStatusContext);

  // Filter out already-solved categories on initial load
  const getUnsolvedGameData = () => {
    const solvedCategories = solvedGameData.map(data => data.category);
    return gameData.filter(data => !solvedCategories.includes(data.category));
  };

  const unsolvedData = getUnsolvedGameData();
  const initialShuffled = unsolvedData.length > 0 ? shuffleGameData({ gameData: unsolvedData }) : [];

  const [shuffledRows, setShuffledRows] = React.useState(initialShuffled);
  const [flatWordsList, setFlatWordsList] = React.useState(
    initialShuffled.flat()
  );
  const [isEndGameModalOpen, setisEndGameModalOpen] = React.useState(false);
  const [gridShake, setGridShake] = React.useState(false);
  const [showConfetti, setShowConfetti] = React.useState(false);
  const [animatingWords, setAnimatingWords] = React.useState([]);
  const [mergingWords, setMergingWords] = React.useState([]);
  const isInitialMount = React.useRef(true);

  function resetGame() {
    trackPlayAgain();
    const newShuffledRows = shuffleGameData({ gameData });
    setGuessCandidate([]);
    setSubmittedGuesses([]);
    setSolvedGameData([]);
    setAnimatingWords([]);
    setMergingWords([]);
    setShuffledRows(newShuffledRows);
    setFlatWordsList(newShuffledRows.flat());
    setIsGameOver(false);
    setIsGameWon(false);
    setisEndGameModalOpen(false);
    isInitialMount.current = true; // Reset to initial mount state
  }

  // use effect to update Game Grid after a row has been correctly solved
  React.useEffect(() => {
    // Skip on initial mount to avoid animating already-solved data
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (solvedGameData.length === 0) return;

    // Get the most recently solved words
    const latestSolved = solvedGameData[solvedGameData.length - 1];

    // Reorder the flat list: put solved words at the front (positions 0-3)
    const solvedWords = latestSolved.words;
    const remainingWords = flatWordsList.filter(word => !solvedWords.includes(word));
    const reorderedList = [...solvedWords, ...remainingWords];

    // Update the list - framer-motion will animate the reordering
    setFlatWordsList(reorderedList);
    setAnimatingWords(solvedWords);

    // After grid reorders, start merge animation and remove words simultaneously
    const mergeDelay = window.setTimeout(() => {
      setMergingWords(solvedWords);
      // Remove at the same time so success row doesn't push anything down
      setFlatWordsList(remainingWords);
      setAnimatingWords([]);
      setMergingWords([]);
    }, 800);

    return () => {
      window.clearTimeout(mergeDelay);
    };
  }, [solvedGameData]);

  // Handle End Game!
  React.useEffect(() => {
    if (!isGameOver) {
      return;
    }

    // Track game completion
    const numMistakes = submittedGuesses.length - solvedGameData.length;
    if (isGameWon) {
      trackGameWon(submittedGuesses.length, numMistakes);
    } else {
      trackGameLost(submittedGuesses.length, numMistakes);
    }

    let confettiDelay;
    if (isGameWon) {
      // Delay confetti to match success row appearance
      confettiDelay = window.setTimeout(() => {
        setShowConfetti(true);
      }, 800);
    }

    // extra delay for game won to allow confetti to show
    const modalDelay = isGameWon ? 2800 : 250;
    const delayModalOpen = window.setTimeout(() => {
      setisEndGameModalOpen(true);
      //unmount confetti after modal opens
      setShowConfetti(false);
    }, modalDelay);

    return () => {
      window.clearTimeout(delayModalOpen);
      if (confettiDelay) window.clearTimeout(confettiDelay);
    };
  }, [isGameOver, isGameWon]);

  return (
    <>
      <div className={`game-wrapper`}>
        <p className="text-center text-[#6B7F5E] mb-2 tracking-wide font-medium">
          Create four groups of four!
        </p>
        {isGameOver && isGameWon ? (
          <GameWonModal
            open={isEndGameModalOpen}
            submittedGuesses={submittedGuesses}
          />
        ) : (
          <GameLostModal
            open={isEndGameModalOpen}
            submittedGuesses={submittedGuesses}
            onPlayAgain={resetGame}
          />
        )}
        <GameGrid
          flatWordsList={flatWordsList}
          shouldGridShake={gridShake}
          setShouldGridShake={setGridShake}
          animatingWords={animatingWords}
          mergingWords={mergingWords}
        />
        {showConfetti && isGameOver && (
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-50">
            <ConfettiExplosion
              particleCount={100}
              force={0.8}
              duration={2500}
            />
          </div>
        )}
        {!isGameOver ? (
          <>
            <NumberOfMistakesDisplay />
            <GameControlButtonsPanel
              flatWordsList={flatWordsList}
              setFlatWordsList={setFlatWordsList}
              setGridShake={setGridShake}
            />
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <ViewResultsModal />
            <Button
              variant="secondary"
              onClick={resetGame}
              className="w-full"
            >
              <RotateCcw className="h-4 w-4 mr-2" strokeWidth={1} />
              <p className="select-none font-medium">Play Again</p>
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default Game;
